import React, { useState, useEffect, useRef } from "react";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import { FaEye, FaDownload, FaCopy, FaChartBar } from "react-icons/fa";
import FilterBuilder from "./FilterBuilder";
import AdvancedFilterBuilder from "./AdvancedFilterBuilder";
import DocumentGrid from "./DocumentGrid";
import SortControl from "./SortControl";
import ColumnSelector, { loadColumnPrefs, saveColumnPrefs } from "./ColumnSelector";
import AuditoriaModal from "./AuditoriaModal";
import { useDocumentService } from "../services/api";
import PDFViewer from "./PDFViewer";
import { useNotification } from "../hooks/useNotification";
import { formatDate } from "../utils/dateUtils";
import { buildCondition, findField, formatDateToISO } from "../config/searchFields";

const Buscador = ({
  tipoSeleccionado,
  onFiltrarResultados,
  paginacion,
  setPaginacion,
  onSearchOverride,
  customColumns,
  customActions,
  extraActions = [],
  hideDefaultActions = false,
  forcedMetadataSpecs, // Opcional: para pasar specs pre-cargadas
  allowEmptySearch = false, // Permitir buscar sin filtros
  metadataEditable = false, // Permitir edición de metadatos en la grilla
  onMetadataEdit, // Callback para la edición de metadatos
  refreshTrigger = 0, // Prop para forzar recarga desde el padre
  selectable = false, // Habilitar selección por checkboxes
  selectedIds = [], // IDs de documentos seleccionados
  onSelectionChange, // Callback al cambiar selección
  optionalColumns = [], // Columnas que el usuario puede mostrar u ocultar
  columnPrefsKey, // Clave para recordar esa elección entre sesiones
  // Campos del sistema que ofrece este buscador. Los decide la pantalla, no el componente.
  //
  // El default vacío es deliberado. Antes el catálogo se importaba acá adentro, y como
  // `Buscador` es compartido entre el árbol documental y Control de Calidad, los once campos
  // se filtraron a las cinco pestañas de Control de Calidad — donde la mayoría no puede tener
  // valor. ADR-040 había dejado esa pantalla explícitamente fuera de alcance (issue UBA #43).
  // Con default vacío, quien no los pide no los hereda.
  systemFields = []
}) => {
  const documentService = useDocumentService();
  const notify = useNotification();
  const [filtros, setFiltros] = useState([]); // Lista de filtros dinámicos (modo simple)
  const [advancedFilters, setAdvancedFilters] = useState({ filterGroups: [], groupOperator: "OR" }); // Filtros modo avanzado
  const [sortConfig, setSortConfig] = useState([]); // Configuración de ordenamiento
  const [searchMode, setSearchMode] = useState("simple"); // "simple" o "advanced"
  const [mensaje, setMensaje] = useState("");
  const [resultados, setResultados] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [busquedaIniciada, setBusquedaIniciada] = useState(false);
  const [auditoriaEventos, setAuditoriaEventos] = useState([]);
  const [auditoriaError, setAuditoriaError] = useState(null);
  const [mostrarAuditoria, setMostrarAuditoria] = useState(false);
  // Sólo los metadatos de la serie. Los campos del sistema NO se guardan en estado: llegan
  // por prop y pueden cambiar sin que cambie la serie —en Control de Calidad cambian al
  // cambiar de pestaña—, así que se combinan al renderizar. Meterlos en el estado obligaría a
  // que el efecto que trae la spec dependiera de la prop, y como es un array nuevo en cada
  // render, eso dispararía un refetch infinito.
  const [camposMetadata, setCamposMetadata] = useState([]);
  const [metadataSpecs, setMetadataSpecs] = useState([]); // Especificaciones completas para MetadataDisplay
  const isInitialLoad = useRef(true);
  // Issue UBA #56. La paginación vive en el padre y sobrevive a todo: al cambio de pestaña, al de
  // serie y hasta al remonte del buscador. Si el usuario estaba en la página 3 y aplica un filtro
  // que matchea menos documentos que el offset, el servidor devuelve cero filas sobre un `total`
  // que no es cero, y la pantalla dice "No se encontraron resultados". Peor: queda pegado, porque
  // el servidor hace eco de la página pedida y acá se vuelve a guardar. Hasta ahora lo único que
  // devolvía la página a 0 era el botón "Restablecer" — que fue el rodeo que encontró testing.
  //
  // `ultimaPaginacionRef` distingue por qué corrió el efecto de abajo: si cambió la página o el
  // tamaño, el offset es lo que el usuario pidió y se respeta; si cambió cualquier otra cosa
  // —agregar o sacar un filtro—, el criterio es nuevo y hay que volver a la primera página.
  const ultimaPaginacionRef = useRef({ page: paginacion.page, size: paginacion.size });
  // Y este marca el cambio de página que provoca el propio reseteo, para que no dispare una
  // segunda búsqueda idéntica a la que acaba de salir.
  const reseteoPropioRef = useRef(false);
  const [mostrarTabla, setMostrarTabla] = useState(true);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);
  // Columnas opcionales: arrancan apagadas y la elección del usuario se recuerda.
  const [columnPrefs, setColumnPrefs] = useState(() => loadColumnPrefs(columnPrefsKey));
  const [prefsKeyActual, setPrefsKeyActual] = useState(columnPrefsKey);

  // La clave cambia sin que el componente se remonte: en Control de Calidad hay una por
  // pestaña, pero el `key` es la serie (#66, preservar los filtros al cambiar de pestaña).
  // El inicializador de `useState` corre sólo al montar, así que sin esto la elección se
  // guardaría en la clave nueva y se seguiría mostrando la de la pestaña anterior.
  //
  // Va durante el render y no en un `useEffect` a propósito: con efecto se pinta un frame
  // con las columnas de la pestaña que el usuario acaba de dejar.
  if (columnPrefsKey !== prefsKeyActual) {
    setPrefsKeyActual(columnPrefsKey);
    setColumnPrefs(loadColumnPrefs(columnPrefsKey));
  }

  const handleColumnPrefsChange = (prefs) => {
    setColumnPrefs(prefs);
    saveColumnPrefs(columnPrefsKey, prefs);
  };

  const DEFAULT_COLUMNS = [
    {
      key: 'filename',
      label: 'Archivo',
      width: '40%',
      render: (doc) => doc.filename,
      style: { textAlign: 'left', paddingLeft: '80px' },
      sortable: true
    },
    {
      key: 'size',
      label: 'Tamaño (KB)',
      width: '15%',
      render: (doc) => doc.size != null ? Math.round(doc.size / 1024) : '-',
      align: 'center',
      sortable: true
    }
  ];

  const columnasVisibles = [
    ...(customColumns || DEFAULT_COLUMNS),
    ...optionalColumns.filter(col => columnPrefs[col.key])
  ];

  // Lo que ofrecen los builders de filtros: campos del sistema que la pantalla habilitó, más
  // los metadatos filtrables de la serie.
  const camposDinamicos = [...systemFields, ...camposMetadata];

  // Y lo que ofrece el combo de orden: los mismos campos del sistema, menos `signed`, que es
  // un booleano virtual resuelto por presencia de `signed_at` — ordenar por él no significa
  // nada. Los metadatos los agrega SortControl por su cuenta.
  const camposOrdenables = systemFields
    .filter(f => f.name !== 'signed')
    .map(f => ({ key: f.name, label: f.label }));

  // Reset sort when document type changes
  useEffect(() => {
    setSortConfig([]);
  }, [tipoSeleccionado]);

  // Si la pantalla cambia los campos disponibles —en Control de Calidad cambian al cambiar de
  // pestaña, sin que el buscador se remonte (#66)— un filtro cargado sobre un campo que ya no
  // se ofrece seguiría viajando en el payload sin que el usuario pueda verlo ni sacarlo: la
  // grilla vendría filtrada y no habría forma de saber por qué. Se descartan y se avisa.
  //
  // Lo mismo vale para el ORDEN, que viaja en el mismo payload: un orden por "Fecha de rechazo"
  // sobrevivía al pasar a la pestaña de OCR, donde ese campo es NULL en todas las filas, y el
  // chip mostraba `rejectedAt` —el nombre interno, en inglés— porque `SortControl` cae al key
  // crudo cuando no encuentra el campo. El servidor no falla, así que el síntoma era una lista
  // ordenada de forma arbitraria sin ninguna señal de error.
  //
  // La dependencia es la lista de nombres serializada, no el array: `camposDinamicos` se
  // deriva en cada render y su identidad cambia siempre.
  const nombresDisponibles = camposDinamicos.map((f) => f.name).join('|');
  useEffect(() => {
    if (!nombresDisponibles) return; // todavía no cargó la spec: no hay nada que evaluar
    const disponibles = new Set(nombresDisponibles.split('|'));

    const ordenHuerfano = sortConfig.filter((s) => !disponibles.has(s.field));
    if (ordenHuerfano.length > 0) {
      setSortConfig(sortConfig.filter((s) => disponibles.has(s.field)));
    }

    const sigueDisponible = (f) => disponibles.has(f.fieldName);
    const huerfanos = filtros.filter((f) => f.fieldName && !sigueDisponible(f));

    const gruposLimpios = (advancedFilters.filterGroups || []).map((g) => ({
      ...g,
      conditions: (g.conditions || []).filter((c) => !c.fieldName || sigueDisponible(c)),
    }));
    const huerfanosAvanzados =
      (advancedFilters.filterGroups || []).reduce((n, g) => n + (g.conditions || []).length, 0)
      - gruposLimpios.reduce((n, g) => n + g.conditions.length, 0);

    if (huerfanos.length === 0 && huerfanosAvanzados === 0) {
      // El orden sí se limpió: avisarlo igual, o la lista cambia de criterio sin explicación.
      if (ordenHuerfano.length > 0) notify.info('Se quitó un orden que no aplica en esta vista.');
      return;
    }

    if (huerfanos.length > 0) setFiltros(filtros.filter(sigueDisponible));
    if (huerfanosAvanzados > 0) {
      setAdvancedFilters({ ...advancedFilters, filterGroups: gruposLimpios });
    }

    const total = huerfanos.length + huerfanosAvanzados + ordenHuerfano.length;
    notify.info(
      total === 1
        ? 'Se quitó un filtro que no aplica en esta vista.'
        : `Se quitaron ${total} criterios que no aplican en esta vista.`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nombresDisponibles]);

  // Handle sort change
  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    // Trigger search with new sort
    handleBuscar(newSortConfig);
  };

  // Callback para cuando FilterBuilder (simple) actualiza los filtros
  const handleFiltersChange = (newFilters) => {
    setFiltros(newFilters);
  };

  // Callback para cuando AdvancedFilterBuilder actualiza los filtros
  const handleAdvancedFiltersChange = (newAdvancedFilters) => {
    setAdvancedFilters(newAdvancedFilters);
  };

  // Toggle entre modo simple y avanzado — preserva estado + promueve simple→avanzado
  const toggleSearchMode = () => {
    setSearchMode(prev => {
      if (prev === "simple") {
        // Si hay filtros simples y el modo avanzado está vacío, promover (AND)
        const advancedIsEmpty =
          advancedFilters.filterGroups.length === 0 ||
          advancedFilters.filterGroups.every(g => (g.conditions || []).length === 0);
        if (filtros.length > 0 && advancedIsEmpty) {
          const conditions = filtros.map(f => ({
            id: `promoted_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            fieldName: f.fieldName,
            operator: f.operator,
            value: f.value,
            value2: f.value2 || ''
          }));
          setAdvancedFilters({
            filterGroups: [{ id: `g_${Date.now()}`, operator: 'AND', conditions }],
            groupOperator: 'OR'
          });
        }
      }
      // avanzado → simple: no sobreescribimos filtros simples; el usuario vuelve a lo que tenía
      return prev === "simple" ? "advanced" : "simple";
    });
  };

  // Construir payload de filterGroups según API
  const buildFilterGroupsPayload = (filters) => {
    const conditions = [];

    filters.forEach(filter => {
      if (!filter.fieldName || !filter.value) return;

      // Se busca en camposDinamicos, no en metadataSpecs: ahí viven tanto los metadatos
      // filtrables de la serie como los campos de sistema (firma), y es además el único
      // conjunto que el builder ofrece.
      const field = findField(camposDinamicos, filter.fieldName);
      if (!field) return;

      const condition = buildCondition(filter, field, formatDateToISO);
      if (!condition) return;

      conditions.push(condition);
    });

    if (conditions.length === 0) return null;

    return {
      filterGroups: [
        {
          operator: "AND",
          conditions: conditions
        }
      ]
    };
  };

  // Construir payload según el modo
  const buildPayload = () => {
    if (searchMode === "simple") {
      return buildFilterGroupsPayload(filtros);
    } else {
      // Modo avanzado
      return buildAdvancedPayload(advancedFilters);
    }
  };

  // Construir payload para modo avanzado
  const buildAdvancedPayload = (advFilters) => {
    const { filterGroups, groupOperator } = advFilters;

    if (!filterGroups || filterGroups.length === 0) return null;

    const processedGroups = [];

    filterGroups.forEach(group => {
      const conditions = [];

      group.conditions.forEach(filter => {
        if (!filter.fieldName || !filter.value) return;

        const field = findField(camposDinamicos, filter.fieldName);
        if (!field) return;

        const condition = buildCondition(filter, field, formatDateToISO);
        if (!condition) return;

        conditions.push(condition);
      });

      if (conditions.length > 0) {
        processedGroups.push({
          operator: group.operator,
          conditions: conditions
        });
      }
    });

    if (processedGroups.length === 0) return null;

    return {
      filterGroups: processedGroups,
      groupOperator: groupOperator
    };
  };

  /**
   * @param customSort orden a usar; si no es un array se toma el del estado (el `onClick` del
   *   botón entrega el evento acá, y por eso la comprobación).
   * @param opciones `{ mantenerPagina }` — sólo el efecto de paginación lo pide, que es el único
   *   caso donde el offset actual sigue siendo válido. Todo lo demás es un criterio nuevo y
   *   arranca en la primera página (issue UBA #56).
   */
  const handleBuscar = async (customSort, opciones) => {
    const sortToUse = Array.isArray(customSort) ? customSort : sortConfig;
    const mantenerPagina = opciones?.mantenerPagina === true;
    setBusquedaIniciada(true);
    if (!tipoSeleccionado || typeof tipoSeleccionado !== "string" || tipoSeleccionado.trim() === "") {
      setMensaje("Seleccioná un tipo de documento.");
      return;
    }

    // Validar según el modo, a menos que se permita búsqueda vacía
    if (!allowEmptySearch) {
      if (searchMode === "simple") {
        if (filtros.length === 0) {
          setMensaje("Debe agregar al menos un filtro.");
          return;
        }
      } else {
        if (!advancedFilters.filterGroups || advancedFilters.filterGroups.length === 0) {
          setMensaje("Debe agregar al menos un grupo con condiciones.");
          return;
        }
      }
    }

    const payload = buildPayload();

    // Si no hay payload (sin filtros) y no se permite búsqueda vacía, mostrar error
    if (!payload && !allowEmptySearch) {
      setMensaje("Debe completar los valores de los filtros.");
      return;
    }

    // Evitar búsquedas duplicadas si ya está cargando
    if (cargando) return;

    // El reseteo va acá y no antes: si la validación cortó, el usuario no llegó a buscar nada y
    // moverlo de página sería gratuito.
    const paginacionEfectiva = mantenerPagina ? paginacion : { ...paginacion, page: 0 };
    if (paginacionEfectiva.page !== paginacion.page) {
      reseteoPropioRef.current = true;
      setPaginacion(paginacionEfectiva);
    }

    setCargando(true);

    try {
      let response;

      if (onSearchOverride) {
        // Usar la función de búsqueda personalizada
        response = await onSearchOverride(payload, paginacionEfectiva, sortToUse);
      } else {
        // Usar servicio de documentos por defecto
        response = await documentService.searchDocuments(
          tipoSeleccionado,
          payload,
          paginacionEfectiva,
          sortToUse
        );
      }

      const data = response.data;

      if (data?.docs?.length > 0) {
        setMensaje("");
        setResultados(data.docs);
        setPaginacion({
          total: data.total ?? 0,
          page: data.page ?? paginacionEfectiva.page,
          size: data.size ?? paginacionEfectiva.size,
        });
        onFiltrarResultados?.(data.docs, {
          total: data.total,
          page: data.page,
          size: data.size,
        });
        // Efecto de fade in después de cargar los datos
        setTimeout(() => {
          setMostrarTabla(true);
        }, 100);
      } else {
        setMensaje("No se encontraron resultados");
        setResultados([]);
        setPaginacion({
          total: data?.total ?? 0,
          page: data?.page ?? paginacionEfectiva.page,
          size: data?.size ?? paginacionEfectiva.size,
        });
        setMostrarTabla(true);
      }
    } catch (error) {
      console.error("Error buscando:", error);
      setMensaje("Error al buscar documentos.");
      setResultados([]);
      setMostrarTabla(true);
    } finally {
      setCargando(false);
    }
  };

  // Al cambiar de página o tamaño, buscar de nuevo
  useEffect(() => {
    const cargarCamposDinamicos = async () => {
      if (!tipoSeleccionado) return;

      try {
        const response = await documentService.getMetadataSpec(tipoSeleccionado);
        const data = response.data;

        const fieldSpec = data.fieldSpec || [];

        // Guardar todas las especificaciones para MetadataDisplay
        setMetadataSpecs(fieldSpec);

        // Si forcedMetadataSpecs está presente, usar esas specs en lugar de las cargadas
        // (Aunque para FiltersBuilder necesitamos las de la API para saber qué es filtrable)
        if (forcedMetadataSpecs) {
          // Si se pasan specs forzadas, podrías optar por combinarlas o usarlas para display
          // Por ahora seguimos cargando de la API para garantizar consistency en los filtros
        }

        // Filtrar solo los campos que tienen uiFilterable: true para los filtros.
        // A los metadatos de la serie se suman los campos de sistema (firma), que no
        // vienen de la spec porque son del documento, no de la serie.
        const camposFiltrables = fieldSpec.filter(campo => campo.uiFilterable === true);
        setCamposMetadata(camposFiltrables);

        // Limpiar filtros al cambiar de tipo de documento
        setFiltros([]);

      } catch (error) {
        console.error("Error al cargar campos dinámicos:", error);
        // Los campos de sistema no dependen de la serie: siguen disponibles aunque
        // falle la carga de la spec.
        setCamposMetadata([]);
        setMetadataSpecs([]);
        setFiltros([]);
      }
    };

    cargarCamposDinamicos();
  }, [tipoSeleccionado]); // Solo depende de tipoSeleccionado

  // Efecto para re-buscar cuando cambia la paginación o el conjunto de filtros
  useEffect(() => {
    const anterior = ultimaPaginacionRef.current;
    const cambioLaPagina = paginacion.page !== anterior.page || paginacion.size !== anterior.size;
    ultimaPaginacionRef.current = { page: paginacion.page, size: paginacion.size };

    // El cambio de página que produjo el propio reseteo ya salió como búsqueda: no repetirla.
    if (reseteoPropioRef.current) {
      reseteoPropioRef.current = false;
      isInitialLoad.current = false;
      return;
    }

    const hasSimpleFilters = filtros.length > 0;
    const hasAdvancedFilters = advancedFilters.filterGroups?.length > 0;
    const shouldSearchOnPageChange = allowEmptySearch || hasSimpleFilters || hasAdvancedFilters;

    // Solo buscar si ya se inició una búsqueda y hay filtros activos o se permite búsqueda vacía
    // Evitar búsquedas innecesarias al cargar inicialmente
    if (busquedaIniciada && shouldSearchOnPageChange && !isInitialLoad.current) {
      // Efecto de fade out antes de buscar
      setMostrarTabla(false);
      setTimeout(() => {
        // Si el efecto corrió porque el usuario pidió otra página, el offset es suyo y se respeta.
        // Si corrió porque cambiaron los filtros, el criterio es nuevo: primera página (#56).
        handleBuscar(undefined, { mantenerPagina: cambioLaPagina });
      }, 150);
    }
    isInitialLoad.current = false;
  }, [paginacion.page, paginacion.size, allowEmptySearch, filtros.length, advancedFilters]);

  // Efecto para recargar cuando cambia el trigger externo
  useEffect(() => {
    if (refreshTrigger > 0) {
      handleBuscar();
    }
  }, [refreshTrigger]);


  const handleRestablecer = () => {
    setFiltros([]);
    setAdvancedFilters({ filterGroups: [], groupOperator: "OR" });
    setResultados([]);
    setMensaje("");
    setBusquedaIniciada(false);
    setPaginacion({ ...paginacion, page: 0 });
  };


  // Verificar si el botón de búsqueda debe estar deshabilitado
  const isSearchDisabled = () => {
    if (allowEmptySearch) return false;

    if (searchMode === "simple") {
      // En modo simple: debe haber al menos un filtro con valor
      return filtros.length === 0 || !filtros.some(f => f.value && f.value.trim() !== '');
    } else {
      // En modo avanzado: debe haber al menos un grupo con al menos una condición con valor
      if (!advancedFilters.filterGroups || advancedFilters.filterGroups.length === 0) {
        return true;
      }
      return !advancedFilters.filterGroups.some(group =>
        group.conditions && group.conditions.length > 0 &&
        group.conditions.some(c => c.value && c.value.trim() !== '')
      );
    }
  };

  const formatearFecha = (fechaISO) => formatDate(fechaISO);

  const handleVer = (docId) => {
    setViewingDocId(docId);
    setShowPDFViewer(true);
  };

  const closePDFViewer = () => {
    setShowPDFViewer(false);
    setViewingDocId(null);
  };


  const handleDescargar = async (idDoc) => {
    try {
      const response = await documentService.downloadDocument(idDoc);

      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      // Intentar obtener el nombre del archivo desde el header "Content-Disposition"
      const contentDisposition = response.headers["content-disposition"];
      let filename = "documento.pdf"; // valor por defecto

      if (contentDisposition && contentDisposition.includes("filename=")) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar memoria
      URL.revokeObjectURL(blobUrl);
      notify.success("Documento descargado correctamente");
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
      notify.error("No se pudo descargar el documento");
    }
  };

  const handleCopiarLink = async (idDoc) => {
    try {
      const downloadUrl = documentService.getDownloadUrl(idDoc);

      await navigator.clipboard.writeText(downloadUrl);
      notify.success("Link de descarga copiado al portapapeles");
    } catch (error) {
      console.error("Error al copiar el link:", error);
      // Fallback para navegadores que no soportan clipboard API
      const downloadUrl = documentService.getDownloadUrl(idDoc);
      const textArea = document.createElement("textarea");
      textArea.value = downloadUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        notify.success("Link de descarga copiado al portapapeles");
      } catch (err) {
        notify.error("No se pudo copiar el link");
      }
      document.body.removeChild(textArea);
    }
  };

  // El modal se abre SIEMPRE, con eventos o con el motivo por el que no los hay. Antes
  // `setMostrarAuditoria(true)` estaba dentro del `try` y después del `await`, así que ante
  // cualquier falla no pasaba nada visible más que un toast genérico: 403, 404, 500 y timeout se
  // veían idénticos. Con la ACL nueva eso habría llegado como "la auditoría no anda" sin que nadie
  // pudiera saber si era permiso, índice o red.
  const handleAuditoria = async (idDoc) => {
    setAuditoriaError(null);
    try {
      const response = await documentService.getAudit(idDoc);
      setAuditoriaEventos(response.data?.events || []);
    } catch (error) {
      console.error("Error al traer auditoría:", error);
      const codigo = error?.response?.status;
      setAuditoriaEventos([]);
      setAuditoriaError(
        codigo === 403 ? "No tenés permiso para ver la auditoría de este documento."
          : codigo === 404 ? "El documento no existe o fue eliminado."
            : "No se pudo consultar la auditoría. Reintentá en un momento."
      );
    }
    setMostrarAuditoria(true);
  };


  const handleFirmar = (doc) => {
    notify.info(`Función de firmado pendiente para el documento: ${doc.filename}`);
  };


  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .metadata-button:hover {
            background-color: #003366 !important;
          }
        `}
      </style>
      <div style={styles.acordeon}>
        <div style={styles.acordeonHeader} onClick={() => setMostrarFiltros(!mostrarFiltros)}>
          {mostrarFiltros ? "▾" : "▸"} <span style={{ color: "white" }}>Filtros</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSearchMode();
            }}
            style={styles.modeToggleButton}
            title={searchMode === "simple" ? "Cambiar a modo avanzado" : "Cambiar a modo simple"}
          >
            {searchMode === "simple" ? "Modo Avanzado →" : "← Modo Simple"}
          </button>
        </div>
        {mostrarFiltros && (
          <div style={styles.acordeonBody}>
            <div style={{ display: "flex", gap: "20px" }}>
              <div style={{ flex: 1 }}>
                {searchMode === "simple" ? (
                  <FilterBuilder
                    metadataSpecs={camposDinamicos}
                    onFiltersChange={handleFiltersChange}
                    initialFilters={filtros}
                    onSuggest={async (fieldName, query) => {
                      if (!tipoSeleccionado) return [];
                      const res = await documentService.getFieldSuggestions(tipoSeleccionado, fieldName, query);
                      return res.data || [];
                    }}
                  />
                ) : (
                  <AdvancedFilterBuilder
                    metadataSpecs={camposDinamicos}
                    onFiltersChange={handleAdvancedFiltersChange}
                    initialGroups={advancedFilters.filterGroups}
                    onSuggest={async (fieldName, query) => {
                      if (!tipoSeleccionado) return [];
                      const res = await documentService.getFieldSuggestions(tipoSeleccionado, fieldName, query);
                      return res.data || [];
                    }}
                  />
                )}
              </div>
            </div>
            {/* botones buscar y restablecer siguen igual */}
            <div style={styles.buttonContainerRight}>
              <button
                onClick={handleBuscar}
                style={{
                  ...styles.searchButton,
                  ...(isSearchDisabled() ? styles.searchButtonDisabled : {})
                }}
                disabled={isSearchDisabled()}
              >
                <AiOutlineSearch /> Buscar
              </button>
              <button onClick={handleRestablecer} style={styles.searchButton}>
                <AiOutlineClose /> Restablecer
              </button>
            </div>
          </div>

        )}
      </div>

      {(mensaje || resultados.length > 0) && (
        <div style={styles.container}>
          {busquedaIniciada && mensaje && resultados.length === 0 && (
            <p style={styles.message}>{mensaje}</p>
          )}

          {/* Grilla de Documentos */}
          <div style={styles.gridControls}>
            <SortControl
              sortConfig={sortConfig}
              onSortChange={handleSortChange}
              metadataSpecs={metadataSpecs}
              systemFields={camposOrdenables}
            />
            <ColumnSelector
              columns={optionalColumns}
              enabled={columnPrefs}
              onChange={handleColumnPrefsChange}
            />
          </div>

          <DocumentGrid
            documents={resultados}
            metadataSpecs={metadataSpecs}
            loading={cargando}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
            columns={columnasVisibles}
            actions={customActions || [ // Si customActions es null/undefined, usa default
              ...(hideDefaultActions ? [] : [
                {
                  key: 'view',
                  icon: <FaEye />,
                  label: 'Ver documento',
                  onClick: (doc) => handleVer(doc.idDoc)
                },
                {
                  key: 'download',
                  icon: <FaDownload />,
                  label: 'Descargar documento',
                  onClick: (doc) => handleDescargar(doc.idDoc)
                },
                {
                  key: 'audit',
                  icon: <FaChartBar />,
                  label: 'Auditoría',
                  onClick: (doc) => handleAuditoria(doc.idDoc)
                }
              ]),
              ...extraActions // Acciones extra siempre se agregan si no se sobrescribe todo customActions
            ]}
            showMetadata={true}
            metadataEditable={metadataEditable}
            onMetadataEdit={onMetadataEdit}
            permissions={{
              canView: true,
              canDownload: true,
              canEditMetadata: metadataEditable
            }}
            pagination={paginacion}
            onPaginationChange={setPaginacion}
            emptyMessage="No se encontraron documentos"
            selectable={selectable}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
          />
        </div>
      )}
      {mostrarAuditoria && (
        <AuditoriaModal
          eventos={auditoriaEventos}
          error={auditoriaError}
          onCerrar={() => setMostrarAuditoria(false)}
        />
      )}

      {/* PDFViewer Modal */}
      {showPDFViewer && viewingDocId && (
        <PDFViewer
          documentId={viewingDocId}
          onClose={closePDFViewer}
          operation="view"
        />
      )}
    </>
  );



};

const styles = {
  gridControls: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "16px",
  },
  acordeon: {
    border: "1px solid #ccc",
    borderRadius: "6px",
    marginBottom: "20px",
    overflow: "hidden",
  },
  acordeonHeader: {
    backgroundColor: "#00468B",
    padding: "12px 16px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modeToggleButton: {
    backgroundColor: "#fff",
    color: "#00468B",
    border: "2px solid #fff",
    borderRadius: "4px",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  acordeonBody: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderTop: "1px solid #ccc",
  },
  container: {
    padding: "20px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    backgroundColor: "#f9f9f9",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "15px",
  },
  label: {
    fontWeight: "bold",
    marginBottom: "5px",
  },
  input: {
    padding: "10px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  },
  buttonContainerRight: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "10px",
  },
  searchButton: {
    backgroundColor: "#00468B",
    color: "#fff",
    padding: "12px 30px",
    border: "none",
    borderRadius: "25px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  searchButtonDisabled: {
    backgroundColor: "#ccc",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  message: {
    color: "red",
    fontSize: "18px",
    marginTop: "20px",
    fontWeight: "bold",
    textAlign: "center",
  },
  tableContainer: {
    marginTop: "20px",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "10px",
    backgroundColor: "#f9f9f9",
    borderRadius: "5px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  th: {
    backgroundColor: "#00468B", // Azul fuerte
    color: "#ffffff",
    padding: "12px",
    fontWeight: "bold",
    textAlign: "center", // ✅ Centrado
    borderBottom: "2px solid #ddd",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #ddd",
    color: "#333",
    fontSize: "14px",
    textAlign: "center", // ✅ Centrado
    whiteSpace: "nowrap",
  },
  trHover: {
    backgroundColor: "#f1f1f1",
    transition: "background-color 0.2s",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    borderRadius: "5px",
  },
  spinnerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    gap: "15px",
  },
  loadingSpinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #00468B",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  spinnerText: {
    color: "#00468B",
    fontSize: "18px",
    fontWeight: "bold",
    fontStyle: "italic",
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px", // Espacio entre botones
  },

  actionButton: {
    backgroundColor: "#00468B",     // Azul UBA
    color: "#fff",                  // Blanco
    border: "none",
    borderRadius: "8px",            // Bordes redondeados
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
  },

  // Opcional: efecto hover
  actionButtonHover: {
    backgroundColor: "#003366",
  },

  auditoriaModal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  auditoriaContenido: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    width: "80%",
    maxWidth: "600px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    position: "relative",  // ← agregá esta línea
  },

  cerrarModal: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "#00468B",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    fontSize: "18px",
    fontWeight: "bold",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    zIndex: 10,
  },

  // Estilos para metadata
  metadataButton: {
    backgroundColor: "#00468B",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "6px 8px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
  },
  metadataCell: {
    padding: "0",
    backgroundColor: "#F8F9FA",
    borderTop: "1px solid #E5E7EB",
  },



};

export default Buscador;
